import { api } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type MachineCodingProjectStatus = 'active' | 'completed' | 'paused';

export interface MachineCodingTestResults {
 passed: number;
 total: number;
 lastRun?: string;
}

/** Payload accepted by create (fork). Mirrors CreateMachineCodingProjectDto. */
export interface CreateMachineCodingProjectInput {
 name: string;                 // required, <= 200
 description?: string;         // <= 1000
 forkedFrom: string;           // required, template id
 fileContents?: { [path: string]: string };
 apiRequests?: any[];          // saved API-tester requests
}

/** Payload accepted by update (autosave / checkpoint). All fields optional. */
export interface UpdateMachineCodingProjectInput {
 name?: string;
 description?: string;
 fileContents?: { [path: string]: string };
 apiRequests?: any[];          // saved API-tester requests
 status?: MachineCodingProjectStatus;
 timeSpent?: number;
 progress?: number;
 testResults?: MachineCodingTestResults;
}

/** A persisted machine-coding project returned by the API. Id is `MCP@<uuid>`. */
export interface MachineCodingProject {
 id: string;
 ownerId: string;
 name: string;
 description: string;
 forkedFrom: string;
 fileContents: { [path: string]: string };
 apiRequests?: any[];
 status: MachineCodingProjectStatus;
 timeSpent: number;
 progress: number;
 testResults: MachineCodingTestResults | null;
 createdAt: string;
 updatedAt: string;
}

const BASE = '/machine-coding/projects';

class MachineCodingProjectService {
 /** List the authenticated user's projects, newest-touched first. */
 async listMine(): Promise<MachineCodingProject[]> {
  return await api.get(BASE);
 }

 /** Fetch a single owned project by id. */
 async getById(id: string): Promise<MachineCodingProject> {
  return await api.get(`${BASE}/${encodeURIComponent(id)}`);
 }

 /** Create (fork) a new project owned by the authenticated user. */
 async create(
  input: CreateMachineCodingProjectInput,
 ): Promise<MachineCodingProject> {
  return await api.post(BASE, input);
 }

 /** Partially update an owned project (autosave or checkpoint). */
 async update(
  id: string,
  input: UpdateMachineCodingProjectInput,
 ): Promise<MachineCodingProject> {
  return await api.patch(`${BASE}/${encodeURIComponent(id)}`, input);
 }

 /** Delete an owned project. */
 async remove(id: string): Promise<void> {
  return await api.delete(`${BASE}/${encodeURIComponent(id)}`);
 }
}

export const machineCodingProjectService = new MachineCodingProjectService();
export default machineCodingProjectService;
